import { StateCreator } from 'zustand';
import { nanoid } from 'nanoid';
import type {
  Character,
  MentalState,
  MutationTracking,
  Mutation,
  PsychicProfile,
  PsychicPower,
  FatePoints,
  WoundsTracking,
  BodyLocation,
  CriticalDamage,
  PermanentInjury,
  FatigueTracking,
  StoredMalignancy,
  StoredMutation
} from '../../../types/character';
import type { SustainedPower } from '../../../types/psychic';
import type {
  NavigatorProfile,
  CharacterNavigatorPower,
  NavigatorMutation,
  NavigatorMasteryLevel
} from '../../../types/navigator';
import { initializeProgression } from '../utils/calculations';
import { generateIPPendingTests, generateCPPendingTests } from '../../../services/ThresholdMonitor';

export interface ProgressionSlice {
  // Mental State
  updateMentalState: (updates: Partial<MentalState>) => void;
  addInsanity: (points: number) => void;
  addCorruption: (points: number) => void;
  removeInsanity: (points: number) => void;
  buybackInsanity: (points: number) => { success: boolean; reason?: string };
  resolvePendingTest: (testId: string) => void;

  // Mutation Tracking
  addMutation: (mutation: Omit<Mutation, 'id' | 'acquiredAt' | 'corruptionWhenAcquired'>) => void;
  removeMutation: (mutationId: string) => void;
  checkForMutations: () => void;

  // Psychic Profile
  updatePsychicProfile: (updates: Partial<PsychicProfile>) => void;
  setPsyRating: (rating: number) => void;
  addPsychicPower: (power: PsychicPower) => void;
  removePsychicPower: (powerId: string) => void;
  recordPsychicPhenomena: (phenomena: string) => void;
  addSustainedPower: (power: Omit<SustainedPower, 'activatedAt'>) => void;
  removeSustainedPower: (powerId: string) => void;
  clearSustainedPowers: () => void;

  // Fate Points
  updateFatePoints: (updates: Partial<FatePoints>) => void;
  spendFatePoint: () => boolean;
  restoreFatePoint: () => void;
  /** Refill the pool to maximum — the start-of-session refresh (RT Core, not a house rule). */
  restoreAllFatePoints: () => void;
  burnFatePoint: () => void;

  // Wounds Tracking
  updateWoundsTracking: (updates: Partial<WoundsTracking>) => void;
  takeDamage: (amount: number, location?: BodyLocation) => void;
  takeLocationDamage: (amount: number, location: BodyLocation) => void;
  healDamage: (amount: number) => void;
  healLocationDamage: (amount: number, location: BodyLocation) => void;
  addCriticalDamage: (critical: Omit<CriticalDamage, 'id' | 'timestamp'>) => void;
  removeCriticalDamage: (criticalId: string) => void;
  addPermanentInjury: (injury: Omit<PermanentInjury, 'id' | 'acquiredAt'>) => void;
  removePermanentInjury: (injuryId: string) => void;
  setUnconsciousState: (unconscious: boolean) => void;

  // Fatigue Tracking
  addFatigue: (amount: number) => void;
  removeFatigue: (amount: number) => void;

  // Navigator Profile
  addNavigatorPower: (power: CharacterNavigatorPower) => void;
  upgradeNavigatorPower: (powerId: string, newLevel: NavigatorMasteryLevel) => void;
  addNavigatorMutation: (mutation: NavigatorMutation) => void;

  // Movement
  recalculateMovement: () => void;
}

interface CharacterStore extends ProgressionSlice {
  character: Character | null;
  hasUnsavedChanges: boolean;
}

export const createProgressionSlice: StateCreator<CharacterStore, [], [], ProgressionSlice> = (
  set,
  get
) => ({
  // Mental State Management
  updateMentalState: (updates: Partial<MentalState>) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          mentalState: {
            ...progression.mentalState,
            ...updates
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  addInsanity: (points: number) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);
    const oldInsanity = progression.mentalState.insanityPoints;
    const newInsanity = Math.min(oldInsanity + points, progression.mentalState.insanityThreshold);

    // Generate pending tests for any IP boundaries crossed
    const newPending = generateIPPendingTests(
      oldInsanity,
      newInsanity,
      progression.mentalState.disorders || []
    );
    const existingPending = progression.mentalState.pendingTests || [];
    const mergedPending = [
      ...existingPending,
      ...newPending.filter(
        (np) => !existingPending.some((ep) => ep.type === np.type && ep.threshold === np.threshold)
      )
    ];

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          mentalState: {
            ...progression.mentalState,
            insanityPoints: newInsanity,
            pendingTests: mergedPending
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });

    // Warning trigger at 75+ threshold
    if (newInsanity >= 75) {
      console.warn(`High insanity warning: ${newInsanity} points`);
    }
  },

  addCorruption: (points: number) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);
    const oldCorruption = progression.mentalState.corruptionPoints;
    const newCorruption = Math.min(
      oldCorruption + points,
      progression.mentalState.corruptionThreshold
    );

    // Generate pending tests for any CP boundaries crossed
    const newPending = generateCPPendingTests(oldCorruption, newCorruption);
    const existingPending = progression.mentalState.pendingTests || [];
    const mergedPending = [
      ...existingPending,
      ...newPending.filter(
        (np) => !existingPending.some((ep) => ep.type === np.type && ep.threshold === np.threshold)
      )
    ];

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          mentalState: {
            ...progression.mentalState,
            corruptionPoints: newCorruption,
            pendingTests: mergedPending
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });

    // Warning trigger at 75+ threshold
    if (newCorruption >= 75) {
      console.warn(`High corruption warning: ${newCorruption} points`);
    }

    // Check for mutations when crossing 10-point thresholds
    const oldThreshold = Math.floor(oldCorruption / 10);
    const newThreshold = Math.floor(newCorruption / 10);
    if (newThreshold > oldThreshold) {
      get().checkForMutations();
    }
  },

  removeInsanity: (points: number) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);
    const newInsanity = Math.max(0, progression.mentalState.insanityPoints - points);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          mentalState: {
            ...progression.mentalState,
            insanityPoints: newInsanity
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  buybackInsanity: (points: number): { success: boolean; reason?: string } => {
    const { character } = get();
    if (!character) return { success: false, reason: 'No character loaded' };
    if (points <= 0) return { success: false, reason: 'Invalid amount' };

    const progression = character.progression || initializeProgression(character);
    const currentIP = progression.mentalState.insanityPoints;

    // Clamp to current IP
    const actualPoints = Math.min(points, currentIP);
    if (actualPoints <= 0) return { success: false, reason: 'No insanity to remove' };

    const xpCost = actualPoints * 100;
    const availableXP = (character.experience?.total ?? 0) - (character.experience?.spent ?? 0);

    if (availableXP < xpCost) {
      return { success: false, reason: `Insufficient XP: need ${xpCost}, have ${availableXP}` };
    }

    const newIP = currentIP - actualPoints;
    const newSpentXP = (character.experience?.spent ?? 0) + xpCost;

    // Preserve disorders — they are NEVER removed by IP buyback
    const disorders = progression.mentalState.disorders;

    set({
      character: {
        ...character,
        experience: {
          ...character.experience!,
          spent: newSpentXP,
          available: (character.experience?.total ?? 0) - newSpentXP,
          transactions: [
            ...(character.experience?.transactions ?? []),
            {
              id: nanoid(),
              type: 'manual' as const,
              amount: -xpCost,
              description: `IP Buyback: removed ${actualPoints} Insanity Point${actualPoints > 1 ? 's' : ''} (100 XP each)`,
              timestamp: new Date(),
              resultingSpentXP: newSpentXP,
              resultingRank: character.rankTracking?.currentRank ?? 1
            }
          ]
        },
        progression: {
          ...progression,
          mentalState: {
            ...progression.mentalState,
            insanityPoints: newIP,
            disorders // Explicitly preserve
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });

    return { success: true };
  },

  resolvePendingTest: (testId: string) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);
    const pendingTests = progression.mentalState.pendingTests || [];

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          mentalState: {
            ...progression.mentalState,
            pendingTests: pendingTests.filter((t) => t.id !== testId)
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  // Mutation Tracking

  addMutation: (mutation: Omit<Mutation, 'id' | 'acquiredAt' | 'corruptionWhenAcquired'>) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    const newMutation: Mutation = {
      ...mutation,
      id: nanoid(),
      acquiredAt: new Date(),
      corruptionWhenAcquired: progression.mentalState.corruptionPoints
    };

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          mutationTracking: {
            ...progression.mutationTracking,
            mutations: [...progression.mutationTracking.mutations, newMutation],
            totalMutations: progression.mutationTracking.totalMutations + 1
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  removeMutation: (mutationId: string) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          mutationTracking: {
            ...progression.mutationTracking,
            mutations: progression.mutationTracking.mutations.filter((m) => m.id !== mutationId)
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  checkForMutations: () => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);
    const currentCorruption = progression.mentalState.corruptionPoints;
    const lastCheck = progression.mutationTracking.lastMutationCheck;

    // Update last mutation check
    set({
      character: {
        ...character,
        progression: {
          ...progression,
          mutationTracking: {
            ...progression.mutationTracking,
            lastMutationCheck: currentCorruption
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });

    // Log mutation check for GM/player awareness
    console.log(
      `Mutation check triggered at corruption ${currentCorruption} (last check: ${lastCheck})`
    );
  },

  // Psychic Profile Management
  updatePsychicProfile: (updates: Partial<PsychicProfile>) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          psychicProfile: {
            ...progression.psychicProfile,
            ...updates
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  setPsyRating: (rating: number) => {
    const { character } = get();
    if (!character) return;

    // Validate: Psy Rating must be 0-10
    const validRating = Math.max(0, Math.min(10, rating));

    const progression = character.progression || initializeProgression(character);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          psychicProfile: {
            ...progression.psychicProfile,
            psyRating: validRating
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  addPsychicPower: (power: PsychicPower) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          psychicProfile: {
            ...progression.psychicProfile,
            powers: [...progression.psychicProfile.powers, power]
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  removePsychicPower: (powerId: string) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          psychicProfile: {
            ...progression.psychicProfile,
            powers: progression.psychicProfile.powers.filter((p) => p.id !== powerId)
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  recordPsychicPhenomena: (phenomena: string) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          psychicProfile: {
            ...progression.psychicProfile,
            phenomena: [...progression.psychicProfile.phenomena, phenomena]
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  addSustainedPower: (power: Omit<SustainedPower, 'activatedAt'>) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);
    const sustainedPowers = progression.psychicProfile.sustainedPowers || [];

    // Don't add if already sustained
    if (sustainedPowers.some((sp) => sp.powerId === power.powerId)) return;

    const newSustainedPower: SustainedPower = {
      ...power,
      activatedAt: Date.now()
    };

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          psychicProfile: {
            ...progression.psychicProfile,
            sustainedPowers: [...sustainedPowers, newSustainedPower]
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });

    // Mirror into activeEffects for the combat-round bar (AC 5.3).
    const full = get() as unknown as {
      addActiveEffect?: (effect: {
        id: string;
        label: string;
        source: string;
        sourceKind: string;
        roundsRemaining: number | null;
        createdAt: number;
      }) => void;
    };
    full.addActiveEffect?.({
      id: `sustained-${power.powerId}`,
      label: `Sustaining: ${power.powerName}`,
      source: 'Psychic sustained power',
      sourceKind: 'sustained-power',
      roundsRemaining: null,
      createdAt: Date.now()
    });
  },

  removeSustainedPower: (powerId: string) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);
    const sustainedPowers = progression.psychicProfile.sustainedPowers || [];

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          psychicProfile: {
            ...progression.psychicProfile,
            sustainedPowers: sustainedPowers.filter((sp) => sp.powerId !== powerId)
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });

    // Mirror into activeEffects for the combat-round bar (AC 5.3).
    const full = get() as unknown as { removeActiveEffect?: (id: string) => void };
    full.removeActiveEffect?.(`sustained-${powerId}`);
  },

  clearSustainedPowers: () => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          psychicProfile: {
            ...progression.psychicProfile,
            sustainedPowers: []
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  // Fate Points Management
  updateFatePoints: (updates: Partial<FatePoints>) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          fatePoints: {
            ...progression.fatePoints,
            ...updates
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  spendFatePoint: (): boolean => {
    const { character } = get();
    if (!character) return false;

    const progression = character.progression || initializeProgression(character);

    // Validate: cannot spend if none available
    if (progression.fatePoints.current <= 0) {
      return false;
    }

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          fatePoints: {
            ...progression.fatePoints,
            current: progression.fatePoints.current - 1
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });

    return true;
  },

  restoreFatePoint: () => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    // Validate: cannot exceed maximum
    const newCurrent = Math.min(progression.fatePoints.current + 1, progression.fatePoints.maximum);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          fatePoints: {
            ...progression.fatePoints,
            current: newCurrent
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  /**
   * Refill the pool to its maximum — the start-of-session refresh.
   *
   * This is the RULEBOOK rule, not a house rule. RT Core: "Spent Fate Points are
   * restored at the beginning of the next gaming session, or possibly under special
   * circumstances in the middle of a game session". `restoreFatePoint` returns one at a
   * time, which is the mid-session GM discretion case; this is the session boundary.
   *
   * Does not touch `maximum`, so a burnt point stays burnt — RT restores *spent* points,
   * and replenishing burnt ones is separately at the GM's discretion.
   */
  restoreAllFatePoints: () => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);
    if (progression.fatePoints.current >= progression.fatePoints.maximum) return;

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          fatePoints: {
            ...progression.fatePoints,
            current: progression.fatePoints.maximum
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  burnFatePoint: () => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    // Burning permanently reduces maximum
    const newMaximum = Math.max(0, progression.fatePoints.maximum - 1);
    const newCurrent = Math.min(progression.fatePoints.current, newMaximum);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          fatePoints: {
            ...progression.fatePoints,
            maximum: newMaximum,
            current: newCurrent
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  // Wounds Tracking
  updateWoundsTracking: (updates: Partial<WoundsTracking>) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          woundsTracking: {
            ...progression.woundsTracking,
            ...updates
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  takeDamage: (amount: number, location?: BodyLocation) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);
    const newCurrent = progression.woundsTracking.current - amount;
    const newTotalDamage = progression.woundsTracking.totalDamage + amount;

    // Check for unconscious state (wounds reach 0)
    const unconscious = newCurrent <= 0;

    // Check for death state (wounds go significantly negative - typically -wounds value)
    const dead = newCurrent <= -progression.woundsTracking.maximum;

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          woundsTracking: {
            ...progression.woundsTracking,
            current: newCurrent,
            totalDamage: newTotalDamage,
            unconscious,
            dead,
            firstAidApplied: false
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });

    // If location specified, also apply location damage
    if (location) {
      get().takeLocationDamage(amount, location);
    }
  },

  takeLocationDamage: (amount: number, location: BodyLocation) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    // Find or create location damage entry
    const locationIndex = progression.woundsTracking.locationDamage.findIndex(
      (ld) => ld.location === location
    );

    let updatedLocationDamage = [...progression.woundsTracking.locationDamage];

    if (locationIndex >= 0) {
      updatedLocationDamage[locationIndex] = {
        ...updatedLocationDamage[locationIndex],
        currentDamage: updatedLocationDamage[locationIndex].currentDamage + amount
      };
    } else {
      updatedLocationDamage.push({
        location,
        currentDamage: amount,
        criticalEffects: []
      });
    }

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          woundsTracking: {
            ...progression.woundsTracking,
            locationDamage: updatedLocationDamage
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  healDamage: (amount: number) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    // Validate: cannot exceed maximum
    const newCurrent = Math.min(
      progression.woundsTracking.current + amount,
      progression.woundsTracking.maximum
    );
    const healedAmount = newCurrent - progression.woundsTracking.current;
    const newTotalDamage = Math.max(0, progression.woundsTracking.totalDamage - healedAmount);

    // Clear unconscious/dead states if healing above 0
    const unconscious = newCurrent <= 0;
    const dead = newCurrent <= -progression.woundsTracking.maximum;

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          woundsTracking: {
            ...progression.woundsTracking,
            current: newCurrent,
            totalDamage: newTotalDamage,
            unconscious,
            dead
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  healLocationDamage: (amount: number, location: BodyLocation) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    const locationIndex = progression.woundsTracking.locationDamage.findIndex(
      (ld) => ld.location === location
    );

    if (locationIndex < 0) return;

    let updatedLocationDamage = [...progression.woundsTracking.locationDamage];
    const newDamage = Math.max(0, updatedLocationDamage[locationIndex].currentDamage - amount);

    if (newDamage === 0) {
      // Remove location damage entry if fully healed
      updatedLocationDamage = updatedLocationDamage.filter((ld) => ld.location !== location);
    } else {
      updatedLocationDamage[locationIndex] = {
        ...updatedLocationDamage[locationIndex],
        currentDamage: newDamage
      };
    }

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          woundsTracking: {
            ...progression.woundsTracking,
            locationDamage: updatedLocationDamage
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  addCriticalDamage: (critical: Omit<CriticalDamage, 'id' | 'timestamp'>) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    const newCritical: CriticalDamage = {
      ...critical,
      id: nanoid(),
      timestamp: new Date()
    };

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          woundsTracking: {
            ...progression.woundsTracking,
            criticalDamage: [...progression.woundsTracking.criticalDamage, newCritical]
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  removeCriticalDamage: (criticalId: string) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          woundsTracking: {
            ...progression.woundsTracking,
            criticalDamage: progression.woundsTracking.criticalDamage.filter(
              (c) => c.id !== criticalId
            )
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  addPermanentInjury: (injury: Omit<PermanentInjury, 'id' | 'acquiredAt'>) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    const newInjury: PermanentInjury = {
      ...injury,
      id: nanoid(),
      acquiredAt: new Date()
    };

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          woundsTracking: {
            ...progression.woundsTracking,
            permanentInjuries: [...progression.woundsTracking.permanentInjuries, newInjury]
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  removePermanentInjury: (injuryId: string) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          woundsTracking: {
            ...progression.woundsTracking,
            permanentInjuries: progression.woundsTracking.permanentInjuries.filter(
              (i) => i.id !== injuryId
            )
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  setUnconsciousState: (unconscious: boolean) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          woundsTracking: {
            ...progression.woundsTracking,
            unconscious
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  // Fatigue Tracking

  addFatigue: (amount: number) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);
    // Fatigue can exceed maximum (Toughness Bonus) — this triggers collapse
    const newCurrent = progression.fatigueTracking.current + amount;

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          fatigueTracking: {
            ...progression.fatigueTracking,
            current: newCurrent
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  removeFatigue: (amount: number) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);
    const newCurrent = Math.max(0, progression.fatigueTracking.current - amount);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          fatigueTracking: {
            ...progression.fatigueTracking,
            current: newCurrent
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  // Navigator Profile

  addNavigatorPower: (power: CharacterNavigatorPower) => {
    const { character } = get();
    if (!character?.progression?.navigatorProfile) return;
    const existing = character.progression.navigatorProfile.powers;
    if (existing.some((p) => p.id === power.id)) return;
    set({
      character: {
        ...character,
        progression: {
          ...character.progression,
          navigatorProfile: {
            ...character.progression.navigatorProfile,
            powers: [...existing, power]
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  upgradeNavigatorPower: (powerId: string, newLevel: NavigatorMasteryLevel) => {
    const { character } = get();
    if (!character?.progression?.navigatorProfile) return;
    set({
      character: {
        ...character,
        progression: {
          ...character.progression,
          navigatorProfile: {
            ...character.progression.navigatorProfile,
            powers: character.progression.navigatorProfile.powers.map((p) =>
              p.id === powerId ? { ...p, mastery: newLevel } : p
            )
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  addNavigatorMutation: (mutation: NavigatorMutation) => {
    const { character } = get();
    if (!character?.progression?.navigatorProfile) return;
    set({
      character: {
        ...character,
        progression: {
          ...character.progression,
          navigatorProfile: {
            ...character.progression.navigatorProfile,
            mutations: [...character.progression.navigatorProfile.mutations, mutation]
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  // Movement
  recalculateMovement: () => {
    const { character } = get();
    if (!character) return;

    // Logic to recalculate movement based on agility and traits
    // For now, just a placeholder or simple calculation if needed
    // The actual calculation is done in calculateMovementRates in calculations.ts
    // and applied during updateCharacteristics
  }
});
